// src/components/jobs/JobManagementDashboard.jsx
import React, { useState, useEffect } from 'react';
import { 
  Briefcase, Search, Filter, Plus, Clock, DollarSign, 
  Users, Eye, Edit3, Trash2, MessageSquare, CheckCircle,
  XCircle, AlertTriangle, Calendar, MapPin, Star, X,
  FileText, Mail, Phone, Award, Target, Paperclip, ThumbsUp,
  ThumbsDown, User, ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { jobService } from '../../services/jobService';

const JobManagementDashboard = () => {
  const { userProfile, initialLoading } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    category: 'all',
    sortBy: 'newest'
  });

  const statusConfig = {
    open: { label: 'Open', color: 'bg-green-100 text-green-800', icon: Clock },
    in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-800', icon: Users },
    completed: { label: 'Completed', color: 'bg-gray-100 text-gray-800', icon: CheckCircle },
    cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle },
    paused: { label: 'Paused', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle }
  };

  useEffect(() => {
    if (!initialLoading && userProfile?.organizationId) {
    loadJobs();
    }
  }, [userProfile, initialLoading, filters]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const jobFilters = {
        organizationId: userProfile.organizationId,
        ...(filters.status !== 'all' && { status: filters.status })
      };

      let jobsData = await jobService.getJobs(jobFilters);

      // Fetch applications for each job
      const jobsWithApplications = await Promise.all(
        jobsData.map(async (job) => {
          try {
            const applications = await jobService.getApplicationsByJob(job.id);
            const activeApplications = applications.filter(app => app.status !== 'withdrawn');
            return { ...job, applications: activeApplications };
          } catch (error) {
            console.error(`Error fetching applications for job ${job.id}:`, error);
            return { ...job, applications: [] };
          }
        })
      );

      // Apply additional filters
      let filteredJobs = jobsWithApplications;
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredJobs = filteredJobs.filter(job => 
          job.title.toLowerCase().includes(searchLower) ||
          job.description.toLowerCase().includes(searchLower) ||
          job.category.toLowerCase().includes(searchLower)
        );
      }

      if (filters.category !== 'all') {
        filteredJobs = filteredJobs.filter(job => job.category === filters.category);
      }

      // Sort jobs
      switch (filters.sortBy) {
        case 'newest':
          filteredJobs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          break;
        case 'oldest':
          filteredJobs.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          break;
        case 'budget-high':
          filteredJobs.sort((a, b) => b.budget - a.budget);
          break;
        case 'budget-low':
          filteredJobs.sort((a, b) => a.budget - b.budget);
          break;
        case 'deadline':
          filteredJobs.sort((a, b) => {
            if (!a.deadline) return 1;
            if (!b.deadline) return -1;
            return new Date(a.deadline) - new Date(b.deadline);
          });
          break;
      }

      setJobs(filteredJobs);
    } catch (error) {
      setError('Failed to load jobs');
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (jobId, newStatus) => {
    try {
      await jobService.updateJob(jobId, { status: newStatus });
      loadJobs();
    } catch (error) {
      console.error('Error updating job status:', error);
    }
  };

  const handleViewApplication = async (application, job) => {
    try {
      // Mark as viewed
      await jobService.markApplicationViewed(application.id);
      setSelectedApplication({ ...application, job });
      setShowApplicationModal(true);
    } catch (error) {
      console.error('Error viewing application:', error);
    }
  };

  const handleApplicationAction = async (applicationId, action) => {
    try {
      await jobService.updateApplicationStatus(applicationId, action);
      setShowApplicationModal(false);
      setSelectedApplication(null);
      loadJobs();
    } catch (error) {
      console.error('Error updating application:', error);
    }
  };

  const calculateStats = () => {
    return {
      total: jobs.length,
      open: jobs.filter(j => j.status === 'open').length,
      inProgress: jobs.filter(j => j.status === 'in_progress').length,
      completed: jobs.filter(j => j.status === 'completed').length,
      totalBudget: jobs.reduce((sum, job) => sum + (job.budget || 0), 0),
      avgBudget: jobs.length > 0 ? jobs.reduce((sum, job) => sum + (job.budget || 0), 0) / jobs.length : 0
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Management</h1>
          <p className="text-gray-600">Manage and track your job postings</p>
        </div>
        <Link
          to="/jobs/create"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center"
        >
          <Plus className="mr-2" size={16} />
          Post New Job
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Jobs</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-green-600">{stats.open}</div>
          <div className="text-sm text-gray-600">Open</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-blue-600">{stats.inProgress}</div>
          <div className="text-sm text-gray-600">In Progress</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-gray-600">{stats.completed}</div>
          <div className="text-sm text-gray-600">Completed</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-gray-900">
            R{stats.totalBudget.toLocaleString()}
          </div>
          <div className="text-sm text-gray-600">Total Budget</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-gray-900">
            R{Math.round(stats.avgBudget).toLocaleString()}
          </div>
          <div className="text-sm text-gray-600">Avg Budget</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search jobs..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="paused">Paused</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              <option value="Administrative Support">Administrative Support</option>
              <option value="Data Entry">Data Entry</option>
              <option value="Customer Service">Customer Service</option>
              <option value="Content Creation">Content Creation</option>
              <option value="Social Media Management">Social Media Management</option>
              <option value="Bookkeeping">Bookkeeping</option>
              <option value="Research">Research</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="budget-high">Budget (High to Low)</option>
              <option value="budget-low">Budget (Low to High)</option>
              <option value="deadline">By Deadline</option>
            </select>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <Briefcase className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
          <p className="text-gray-600 mb-4">
            {filters.search || filters.status !== 'all' || filters.category !== 'all'
              ? 'Try adjusting your filters to see more results.'
              : 'Get started by posting your first job.'
            }
          </p>
          <Link
            to="/jobs/create"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="mr-2" size={16} />
            Post Your First Job
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map(job => (
            <JobCard 
              key={job.id} 
              job={job} 
              statusConfig={statusConfig}
              onStatusUpdate={handleStatusUpdate}
              onViewApplication={handleViewApplication}
            />
          ))}
        </div>
      )}

      {/* Application Modal */}
      {showApplicationModal && selectedApplication && (
        <ApplicationModal
          application={selectedApplication}
          onClose={() => {
            setShowApplicationModal(false);
            setSelectedApplication(null);
          }}
          onAction={handleApplicationAction}
        />
      )}
    </div>
  );
};

// Job Card Component
const JobCard = ({ job, statusConfig, onStatusUpdate, onViewApplication }) => {
  const statusInfo = statusConfig[job.status] || statusConfig.open;
  const StatusIcon = statusInfo.icon;
  const hasApplications = job.applications && job.applications.length > 0;
  const pendingApplications = job.applications?.filter(app => app.status === 'pending') || [];

  const formatDate = (dateString) => {
    if (!dateString) return 'No deadline';
    return new Date(dateString).toLocaleDateString('en-ZA');
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{job.title}</h3>
              <p className="text-gray-600 text-sm line-clamp-2">{job.description}</p>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color} flex items-center`}>
                <StatusIcon size={12} className="mr-1" />
                {statusInfo.label}
              </span>
              {job.urgency && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUrgencyColor(job.urgency)}`}>
                  {job.urgency.charAt(0).toUpperCase() + job.urgency.slice(1)} Priority
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
            <div className="flex items-center">
              <DollarSign size={14} className="mr-1" />
              R{job.budget?.toLocaleString()} {job.budgetType === 'hourly' && '/hr'}
            </div>
            <div className="flex items-center">
              <Calendar size={14} className="mr-1" />
              {formatDate(job.deadline)}
            </div>
            <div className="flex items-center">
              <Users size={14} className="mr-1" />
              <span className={hasApplications ? 'font-medium text-blue-600' : ''}>
                {job.applications?.length || 0} Application{job.applications?.length !== 1 ? 's' : ''}
              </span>
              {pendingApplications.length > 0 && (
                <span className="ml-1 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {pendingApplications.length} new
                </span>
              )}
            </div>
            <div className="flex items-center">
              <MapPin size={14} className="mr-1" />
              {job.location || 'Remote'}
            </div>
          </div>

          {job.skillsRequired && job.skillsRequired.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {job.skillsRequired.slice(0, 3).map(skill => (
                <span key={skill} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                  {skill}
                </span>
              ))}
              {job.skillsRequired.length > 3 && (
                <span className="text-gray-500 text-xs px-2 py-1">
                  +{job.skillsRequired.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* Applications Section */}
          {hasApplications && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900">Applications</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {job.applications.slice(0, 3).map(application => (
                  <button
                    key={application.id}
                    onClick={() => onViewApplication(application, job)}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                  >
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User size={14} className="text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{application.vaName}</p>
                        <div className="flex items-center text-xs text-gray-600">
                          <Star size={10} className="text-yellow-400 mr-1" />
                          {application.vaRating || 0} • R{application.proposedRate}/hr
                        </div>
                      </div>
                    </div>
                    <div className="ml-2">
                      {application.status === 'pending' && (
                        <span className="w-2 h-2 bg-blue-600 rounded-full block"></span>
                      )}
                      {application.status === 'accepted' && (
                        <CheckCircle size={14} className="text-green-600" />
                      )}
                      {application.status === 'rejected' && (
                        <XCircle size={14} className="text-red-600" />
                      )}
                    </div>
                  </button>
                ))}
                {job.applications.length > 3 && (
                  <div className="flex items-center justify-center p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                    +{job.applications.length - 3} more
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-row lg:flex-col gap-2">
          <Link
            to={`/jobs/${job.id}`}
            className="flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
          >
            <Eye size={14} className="mr-1" />
            View
          </Link>
          <Link
            to={`/jobs/${job.id}/edit`}
            className="flex items-center justify-center px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
          >
            <Edit3 size={14} className="mr-1" />
            Edit
          </Link>
          {job.assignedVA && (
            <Link
              to={`/communication/chat/${job.assignedVA.id}`}
              className="flex items-center justify-center px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
            >
              <MessageSquare size={14} className="mr-1" />
              Chat
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

// Application Modal Component
const ApplicationModal = ({ application, onClose, onAction }) => {
  const [vaProfile, setVaProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('application');

  useEffect(() => {
    loadVAProfile();
  }, [application.vaId]);

  const loadVAProfile = async () => {
    try {
      const profile = await jobService.getVAProfile(application.vaProfileId);
      setVaProfile(profile);
    } catch (error) {
      console.error('Error loading VA profile:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Application Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('application')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'application'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Application
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'profile'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            VA Profile
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'application' && (
            <div className="space-y-6">
              {/* Applicant Header */}
              <div className="flex items-start justify-between bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <User size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{application.vaName}</h3>
                    <p className="text-gray-600 text-sm">{application.vaTitle}</p>
                    <div className="flex items-center mt-2 space-x-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Star size={14} className="text-yellow-400 mr-1" />
                        {application.vaRating || 0} rating
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={14} className="text-green-600 mr-1" />
                        {application.vaCompletedJobs || 0} jobs completed
                      </div>
                    </div>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  application.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                  application.status === 'accepted' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                </span>
              </div>

              {/* Cover Letter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cover Message</label>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-900 whitespace-pre-wrap">{application.coverMessage}</p>
                </div>
              </div>

              {/* Job Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Job: {application.job.title}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Job Budget:</span>
                    <span className="ml-2 font-medium">R{application.job.budget?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Proposed Rate:</span>
                    <span className="ml-2 font-medium">R{application.proposedRate?.toLocaleString()}/hr</span>
                  </div>
                </div>
              </div>

              {/* Proposed Timeframe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Proposed Timeframe</label>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-900">{application.proposedTimeframe}</p>
                </div>
              </div>

              {/* Experience */}
              {application.experience && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Relevant Experience</label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900 whitespace-pre-wrap">{application.experience}</p>
                  </div>
                </div>
              )}

              {/* Portfolio */}
              {application.portfolio && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Portfolio/Work Samples</label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900 whitespace-pre-wrap">{application.portfolio}</p>
                  </div>
                </div>
              )}

              {/* Questions */}
              {application.questions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Questions/Clarifications</label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900 whitespace-pre-wrap">{application.questions}</p>
                  </div>
                </div>
              )}

              {/* Attachments */}
              {application.attachments && application.attachments.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
                  <div className="space-y-2">
                    {application.attachments.map((file, index) => (
                      <a
                        key={index}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center">
                          <Paperclip size={16} className="text-gray-400 mr-2" />
                          <span className="text-sm text-gray-700">{file.name}</span>
                        </div>
                        <ExternalLink size={16} className="text-blue-600" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="space-y-6">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : vaProfile ? (
                <>
                  {/* View Full Profile Button */}
                  <div className="flex justify-end">
                  <Link
                    to={`/va/${application.vaProfileId}`}
                    target="_blank"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    <ExternalLink className="mr-2" size={16} />
                    View Full Profile
                  </Link>
                  </div>
                  {/* Profile Header */}
                  <div className="flex items-start space-x-4">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                      <User size={32} className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900">{vaProfile.displayName}</h3>
                      <p className="text-gray-600">{vaProfile.title}</p>
                      <div className="flex items-center mt-2 space-x-4 text-sm">
                        <div className="flex items-center">
                          <Star size={14} className="text-yellow-400 mr-1" />
                          {vaProfile.rating || 0} ({vaProfile.totalReviews || 0} reviews)
                        </div>
                        <div className="flex items-center">
                          <Award size={14} className="text-green-600 mr-1" />
                          {vaProfile.completedJobs || 0} jobs completed
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {vaProfile.email && (
                      <div className="flex items-center bg-gray-50 p-3 rounded-lg">
                        <Mail size={16} className="text-gray-400 mr-3" />
                        <div>
                          <p className="text-xs text-gray-600">Email</p>
                          <p className="text-sm text-gray-900">{vaProfile.email}</p>
                        </div>
                      </div>
                    )}
                    {vaProfile.phone && (
                      <div className="flex items-center bg-gray-50 p-3 rounded-lg">
                        <Phone size={16} className="text-gray-400 mr-3" />
                        <div>
                          <p className="text-xs text-gray-600">Phone</p>
                          <p className="text-sm text-gray-900">{vaProfile.phone}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bio */}
                  {vaProfile.bio && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">About</h4>
                      <p className="text-gray-700 whitespace-pre-wrap">{vaProfile.bio}</p>
                    </div>
                  )}

                  {/* Skills */}
                  {vaProfile.skills && vaProfile.skills.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {vaProfile.skills.map(skill => (
                          <span key={skill} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Experience */}
                  {vaProfile.experience && vaProfile.experience.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Experience</h4>
                      <div className="space-y-3">
                        {vaProfile.experience.map((exp, index) => (
                          <div key={index} className="bg-gray-50 p-4 rounded-lg">
                            <h5 className="font-medium text-gray-900">{exp.title}</h5>
                            <p className="text-sm text-gray-600">{exp.company}</p>
                            <p className="text-xs text-gray-500 mt-1">{exp.duration}</p>
                            {exp.description && (
                              <p className="text-sm text-gray-700 mt-2">{exp.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Rate Info */}
                  <div className="grid grid-cols-2 gap-4">
                    {vaProfile.hourlyRate && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">Hourly Rate</p>
                        <p className="text-lg font-bold text-gray-900">R{vaProfile.hourlyRate}/hr</p>
                      </div>
                    )}
                    {vaProfile.availability && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">Availability</p>
                        <p className="text-lg font-bold text-gray-900 capitalize">{vaProfile.availability}</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <User className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                  <p className="text-gray-600">Unable to load VA profile</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {application.status === 'pending' && (
          <div className="flex flex-col sm:flex-row gap-3 p-6 border-t bg-gray-50">
            <button
              onClick={() => onAction(application.id, 'accepted')}
              className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 flex items-center justify-center"
            >
              <ThumbsUp className="mr-2" size={16} />
              Accept Application
            </button>
            <button
              onClick={() => onAction(application.id, 'rejected')}
              className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 flex items-center justify-center"
            >
              <ThumbsDown className="mr-2" size={16} />
              Reject Application
            </button>
            <button
              onClick={onClose}
              className="sm:w-auto bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        )}

        {application.status !== 'pending' && (
          <div className="flex justify-end p-6 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobManagementDashboard;